import React, { useState, useMemo } from 'react';
import { CellInfo } from 'react-table';
import PageContainer from 'src/components/PageContainer';
import PageContent from 'src/components/PageContent';
import NavBar from '../NavBar';
import Flex from 'src/components/Flex';
import Button from 'src/components/Button';
import Table from 'src/components/Table';
import Card from 'src/components/Card';
import Icon from 'src/components/Icon';
import { ICONS } from 'src/utils/constants';
import DateComponent from 'src/components/DateComponent';
import {
    usePromptContexts,
    AiPromptContext,
    useActivatePromptContext,
    useDeactivatePromptContext,
} from 'src/queries/useAiPromptContexts';
import { usePrompts, AiPrompt } from 'src/queries/useAiPrompts';
import { parseError } from 'src/utils';
import FacilityDropDown from 'src/components/FacilityDropDown';
import EditAiPromptContextModal from 'src/components/EditAiPromptContextModal';
import styles from './styles.module.scss';
import { ModalStoreObject, ModalTypes } from 'src/stores/ModalStore';
import { ToastStoreObject } from 'src/stores/ToastStore';

function AiPromptContexts() {
    const [selectedFacility, setSelectedFacility] = useState<number | null>(null);
    const {
        data: promptContexts = [],
        isLoading: contextsLoading,
        error: contextsError,
        refetch: refetchContexts,
    } = usePromptContexts(selectedFacility);
    const { data: prompts = [], isLoading: promptsLoading } = usePrompts();
    const { mutate: activatePromptContext } = useActivatePromptContext();
    const { mutate: deactivatePromptContext } = useDeactivatePromptContext();
    const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
    const [isEditModalOpen, setIsEditModalOpen] = useState(false);
    const [selectedContext, setSelectedContext] = useState<AiPromptContext | null>(null);

    const promptNameMap = useMemo(() => {
        const map = new Map<string, string>();
        prompts.forEach((prompt: AiPrompt) => {
            map.set(prompt.promptId, prompt.promptName);
        });
        return map;
    }, [prompts]);

    const handleFacilityChange = (facility: any) => {
        setSelectedFacility(facility?.value || null);
    };

    const handleActiveToggle = (context: AiPromptContext) => {
        try {
            if (context.actvInd) {
                deactivatePromptContext(context.aiPromptContextId);
            } else {
                activatePromptContext(context.aiPromptContextId);
            }
        } catch (error) {
            ToastStoreObject.show(parseError(error));
        } finally {
            ModalStoreObject.hideModal();
        }
    };

    function handleConfirm(context: AiPromptContext) {
        ModalStoreObject.showModal(ModalTypes.ConfirmationModal, {
            title: `Are you sure you would like to ${context.actvInd ? 'deactivate' : 'activate'} this context?`,
            onConfirm: () => handleActiveToggle(context),
            onCancel: () => ModalStoreObject.hideModal(),
            confirmButtonText: context.actvInd ? 'Deactivate' : 'Activate',
        });
    }

    const columns = [
        {
            Header: 'PROMPT NAME',
            accessor: 'aiPromptId',
            minWidth: 200,
            Cell: (props: CellInfo) => <span>{promptNameMap.get(props.value)}</span>,
        },
        {
            Header: 'STATUS',
            accessor: 'actvInd',
            minWidth: 100,
            Cell: (props: CellInfo) => (
                <span className={props.value ? styles.activeStatus : styles.inactiveStatus}>
                    {props.value ? 'ACTIVE' : 'INACTIVE'}
                </span>
            ),
        },
        {
            Header: 'CREATED',
            accessor: 'insDttm',
            minWidth: 150,
            Cell: (props: CellInfo) => <DateComponent date={props.value} showDateTime={true} />,
        },
        {
            Header: 'UPDATED',
            accessor: 'updDttm',
            minWidth: 150,
            Cell: (props: CellInfo) => <DateComponent date={props.value} showDateTime={true} />,
        },
        {
            Header: 'ACTIONS',
            accessor: 'aiPromptContextId',
            minWidth: 150,
            Cell: (props: CellInfo) => {
                const context = promptContexts.find((c: AiPromptContext) => c.aiPromptContextId === props.value);
                return (
                    <Flex direction="row" align="center">
                        <Button
                            type="small"
                            text="Edit"
                            onClick={() => handleEditContext(context!)}
                            className={styles.actionButton}
                            data-test-id="edit-context-button"
                        />
                        <Button
                            type="small"
                            text={context.actvInd ? 'Deactivate' : 'Activate'}
                            onClick={() => handleConfirm(context)}
                            className={styles.actionButton}
                            data-test-id="delete-context-button"
                        />
                    </Flex>
                );
            },
        },
    ];

    const handleEditContext = (context: AiPromptContext) => {
        setSelectedContext(context);
        setIsEditModalOpen(true);
    };

    const handleCreateContext = () => {
        setIsCreateModalOpen(true);
    };

    const handleCloseCreateModal = () => {
        setIsCreateModalOpen(false);
    };

    const handleCloseEditModal = () => {
        setIsEditModalOpen(false);
        setSelectedContext(null);
    };

    const isLoading = contextsLoading || promptsLoading;
    const error = contextsError;

    return (
        <PageContainer>
            <NavBar />
            <PageContent>
                <Flex direction="column" className={styles.container}>
                    <Flex direction="column" className={styles.header}>
                        <h1 className={styles.pageTitle}>AI Prompt Contexts</h1>
                        <p className={styles.pageSubtitle}>Manage AI prompt contexts for different facilities</p>
                    </Flex>

                    <Flex direction="row" align="center" justify="between" className={styles.controlsRow}>
                        <Flex className={styles.filterWrap}>
                            <FacilityDropDown
                                onChange={handleFacilityChange}
                                selectedValue={selectedFacility}
                                allowEmpty={false}
                                placeholder="Filter by Facility"
                            />
                        </Flex>
                        <Button
                            type="primary"
                            text="Create Context"
                            leftIcon={<Icon name={ICONS.PlusButton} className={styles.plusIcon} />}
                            onClick={handleCreateContext}
                            data-test-id="create-context-button"
                        />
                    </Flex>

                    {error && (
                        <Card className={styles.errorCard} data-test-id="error-card">
                            <div className={styles.errorMessage}>
                                <strong>Error loading prompt contexts:</strong> {parseError(error)}
                                <Button
                                    type="small"
                                    text="Retry"
                                    onClick={() => refetchContexts()}
                                    className={styles.retryButton}
                                    data-test-id="retry-button"
                                />
                            </div>
                        </Card>
                    )}

                    <Card className={styles.tableCard}>
                        <Table
                            columns={columns}
                            data={promptContexts}
                            isLoading={isLoading}
                            noDataText={'No prompt contexts found for the selected facility.'}
                            showPagination={true}
                            defaultPageSize={10}
                        />
                    </Card>
                </Flex>
            </PageContent>

            <EditAiPromptContextModal
                isOpen={isCreateModalOpen}
                onClose={handleCloseCreateModal}
                mode="create"
                facilityId={selectedFacility || undefined}
            />

            <EditAiPromptContextModal
                isOpen={isEditModalOpen}
                onClose={handleCloseEditModal}
                promptContext={selectedContext}
                mode="edit"
            />
        </PageContainer>
    );
}

export default AiPromptContexts;
